import { cn } from "@tellescope/design-system";

import { BlockProps } from "../../../types";
import { useCalendarBlock } from "../../../hooks";
import { BlockHeader } from "../BlockHeader";
import { BlockContent } from "../BlockContent";
import { CalendarFilterPopover } from "./CalendarFilterPopover";
import { CalendarListItem } from "./CalendarListItem";
import { useTranslation } from "react-i18next";
import { BlockPaginatedList } from "../BlockPaginatedList";
import { BlockEmptyState } from "../BlockEmptyState";

export const CalendarBlock = ({
  onMaximize,
  alwaysShowButtons = false,
  className,
  contentClassName,
  onOpenEvent,
  ...rest
}: BlockProps) => {
  const { t } = useTranslation("calendar");
  const {
    parsedInfo,
    events,
    isLoading,
    fetchNextPage,
    filterBlock,
    filterFieldOptions,
    onFilterChange,
    onCancel,
    onNoShow,
    onComplete,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canEditBlock,
    canCreateBlock,
    canDeleteBlock,
  } = useCalendarBlock({ ...rest, onMaximize, alwaysShowButtons, className });

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
        actions={
          canEditBlock && (
            <CalendarFilterPopover
              filterBlock={filterBlock}
              fieldOptions={filterFieldOptions}
              onFilterChange={onFilterChange}
            />
          )
        }
      />

      <BlockContent
        editMode={editMode}
        setEditMode={setEditMode}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-col", contentClassName)}
      >
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("Loading calendar...")}</BlockEmptyState>
          </div>
        ) : !events.length ? (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("No appointments found")}</BlockEmptyState>
          </div>
        ) : (
          <BlockPaginatedList
            className="flex flex-col flex-grow"
            scrollbar="floating"
            fadeWhenClipped
            onBottomReached={() => fetchNextPage()}
          >
            {events.map((event) => (
              <CalendarListItem
                key={event.id}
                title={event.title}
                startTimeInMS={event.startTimeInMS}
                className="mb-2"
                onOpen={onOpenEvent?.(event.id, event.startTimeInMS)}
                onCancel={() => onCancel(event.id)}
                onNoShow={() => onNoShow(event.id)}
                onComplete={() => onComplete(event.id)}
              />
            ))}
          </BlockPaginatedList>
        )}
      </BlockContent>
    </div>
  );
};
